import { memo, useMemo } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import MemberNameCell from './MemberNameCell';
import MembershipStatusCell from './MembershipStatusCell';
import type { Member, MembershipSummary } from '@/types';
import type { MemberPhoto } from '@/types/photo';
import type { DateFormat } from '@/lib';
import { useBulkMemberPhotos } from '@/hooks';
import { useMembershipsToDisplay } from '@/utils';
import { Table, TableWrapper } from '@/components/ui';

interface MembersListViewProps {
  /**
   * The revealed slice of the filtered roster, already split into fixed-size
   * chunks. Each chunk fetches its own photos — see MemberRowChunk.
   */
  chunks: Member[][];
  memberMemberships: Record<string, MembershipSummary[] | undefined>;
  areMembershipsLoading: boolean;
  /** Set when the bulk membership fetch failed — see MembershipStatusCell. */
  membershipsUnavailable?: boolean;
  formatDate: (date: string | Date, formatType?: DateFormat) => string;
}

export default function MembersListView({
  chunks,
  memberMemberships,
  areMembershipsLoading,
  membershipsUnavailable = false,
  formatDate,
}: MembersListViewProps) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg shadow">
      <TableWrapper>
        <Table density="compact">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membership</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member Since</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {chunks.map((chunk, index) => (
              <MemberRowChunk
                // Index-keyed on purpose — see MembersCardView.
                key={`chunk-${index}`}
                members={chunk}
                memberMemberships={memberMemberships}
                areMembershipsLoading={areMembershipsLoading}
                membershipsUnavailable={membershipsUnavailable}
                formatDate={formatDate}
                navigate={navigate}
              />
            ))}
            {chunks.length === 0 && (
              <tr>
                <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                  No members found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}

/**
 * One chunk of rows, owning the batched photo fetch for exactly the members it
 * renders. Renders a fragment so the rows stay direct children of `<tbody>`.
 *
 * The list shows 32px webcam avatars and nothing else, so — unlike the card
 * grid — it needs no uploaded-photo query, and therefore reports no photo
 * status up to the page.
 */
const MemberRowChunk = memo(function MemberRowChunk({
  members,
  memberMemberships,
  areMembershipsLoading,
  membershipsUnavailable,
  formatDate,
  navigate,
}: {
  members: Member[];
  memberMemberships: Record<string, MembershipSummary[] | undefined>;
  areMembershipsLoading: boolean;
  membershipsUnavailable: boolean;
  formatDate: (date: string | Date, formatType?: DateFormat) => string;
  navigate: NavigateFunction;
}) {
  const memberIds = useMemo(() => members.map((member) => member.id), [members]);
  const { data: memberPhotos = {}, isLoading: arePhotosLoading } = useBulkMemberPhotos(memberIds);

  return (
    <>
      {members.map((member) => (
        <MemberRow
          key={member.id}
          member={member}
          memberships={memberMemberships[member.id]}
          photo={memberPhotos[member.id] ?? null}
          arePhotosLoading={arePhotosLoading}
          areMembershipsLoading={areMembershipsLoading}
          membershipsUnavailable={membershipsUnavailable}
          formatDate={formatDate}
          navigate={navigate}
        />
      ))}
    </>
  );
});

const MemberRow = ({
  member,
  memberships,
  photo,
  arePhotosLoading,
  areMembershipsLoading,
  membershipsUnavailable,
  formatDate,
  navigate
}: {
  member: Member;
  memberships: MembershipSummary[] | undefined;
  photo: MemberPhoto | null;
  arePhotosLoading: boolean;
  areMembershipsLoading: boolean;
  membershipsUnavailable: boolean;
  formatDate: (date: string | Date, formatType?: DateFormat) => string;
  navigate: NavigateFunction;
}) => {
  const { memberships: displayMemberships } = useMembershipsToDisplay<MembershipSummary>(memberships);

  return (
    <tr
      onClick={() => navigate(`/members/${member.id}`)}
      className="cursor-pointer hover:bg-gray-50"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <MemberNameCell
          memberId={member.id}
          memberName={`${member.first_name} ${member.last_name}`}
          memberEmail={member.email}
          showPhoto={true}
          showEmail={true}
          photo={photo}
          isPhotoLoading={arePhotosLoading}
        />
      </td>
      <td className="px-6 py-4">
        {areMembershipsLoading ? (
          // Never render a pending fetch as "No Membership" (HOK-OS-50).
          <div className="h-5 w-24 rounded-full bg-gray-200 animate-pulse" />
        ) : displayMemberships.length > 0 ? (
          <div className="space-y-2">
            {displayMemberships.map((membership) => (
              <MembershipStatusCell
                key={membership.id}
                membership={membership}
              />
            ))}
          </div>
        ) : (
          <MembershipStatusCell membership={undefined} isUnavailable={membershipsUnavailable} />
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-gray-500">
          {formatDate(member.created_at, 'date')}
        </div>
      </td>
    </tr>
  );
};
